/**
 * Google Classroom — Standalone wrapper for Google Classroom API.
 *
 * No Vue dependency. Loads gapi + Classroom discovery doc on demand.
 * Uses the same Google Client ID as gma-auth.js.
 *
 * Ported from activities-frontend/src/helpers/google-classroom-auth-strategy.ts
 *
 * @module auth/google-classroom
 */

import { getUserInfo } from './gma-auth.js';

const GOOGLE_CLIENT_ID =
  '81925747312-vao5h4l5bf2c8cbfssff1bejaut2at9n.apps.googleusercontent.com';
const GAPI_SCRIPT_URL = 'https://apis.google.com/js/api.js';
const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const CLASSROOM_DISCOVERY = 'https://classroom.googleapis.com/$discovery/rest?version=v1';

const TEACHER_SCOPE =
  'profile email https://www.googleapis.com/auth/classroom.courses.readonly' +
  ' https://www.googleapis.com/auth/classroom.coursework.students' +
  ' https://www.googleapis.com/auth/classroom.rosters.readonly';

let _accessToken = null;

function withTimeout(promise, ms, msg) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(msg)), ms)),
  ]);
}

// ── Script loading ──────────────────────────────────────────────────────────

function loadScript(url) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${url}`));
    document.head.appendChild(script);
  });
}

async function ensureGapi() {
  await loadScript(GAPI_SCRIPT_URL);
  if (!window.gapi?.client?.classroom) {
    await new Promise((resolve, reject) =>
      gapi.load('client', { callback: resolve, onerror: reject })
    );
    await gapi.client.load(CLASSROOM_DISCOVERY);
  }
}

async function ensureGsi() {
  await loadScript(GSI_SCRIPT_URL);
}

// ── OAuth ───────────────────────────────────────────────────────────────────

/**
 * Prompt the teacher to grant Google Classroom scopes.
 * Resolves with an access token on success.
 * @returns {Promise<string>} access token
 */
export async function grantTeacherScope() {
  await ensureGsi();
  const google = window.google;

  const oauthPromise = new Promise((resolve, reject) => {
    const userInfo = getUserInfo();
    google.accounts.oauth2
      .initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: TEACHER_SCOPE,
        prompt: '',
        login_hint: userInfo?.email || undefined,
        use_fedcm_for_prompt: true,
        callback: (response) => {
          if (response.error) {
            reject(new Error(response.error));
            return;
          }
          _accessToken = response.access_token;
          resolve(_accessToken);
        },
        error_callback: (err) => {
          reject(new Error(err?.message || err?.type || 'Google auth popup failed'));
        },
      })
      .requestAccessToken();
  });

  return withTimeout(oauthPromise, 60000, 'Google Classroom authorization timed out');
}

/** Check if we already have Classroom scopes this session. */
export function hasClassroomAccess() {
  return !!_accessToken;
}

// ── Classroom API ───────────────────────────────────────────────────────────

/**
 * Fetch the teacher's active Google Classroom courses.
 * Calls grantTeacherScope() if not already authorized.
 * @returns {Promise<Array<{id: string, name: string, section?: string}>>}
 */
export async function fetchCourses() {
  if (!_accessToken) await grantTeacherScope();
  await ensureGapi();

  // Set the access token for gapi requests
  gapi.client.setToken({ access_token: _accessToken });

  let courses = [];
  let nextPageToken;
  do {
    const res = await gapi.client.classroom.courses.list({
      teacherId: 'me',
      pageSize: 100,
      pageToken: nextPageToken,
    });
    const active = (res.result.courses || []).filter((c) => c.courseState === 'ACTIVE');
    courses = courses.concat(active);
    nextPageToken = res.result.nextPageToken;
  } while (nextPageToken);

  return courses;
}

/**
 * Post an assignment to Google Classroom.
 * @param {string} courseId - The Google Classroom course ID
 * @param {Object} options
 * @param {string} options.title - Assignment title
 * @param {string} [options.description] - Assignment description
 * @param {string} options.url - Link for students to open
 * @param {string} [options.state='PUBLISHED'] - 'DRAFT' or 'PUBLISHED'
 * @returns {Promise<Object>} The created CourseWork object
 */
export async function createCourseWork(
  courseId,
  { title, description, url, state = 'PUBLISHED', dueDate, dueTime }
) {
  if (!_accessToken) await grantTeacherScope();
  await ensureGapi();
  gapi.client.setToken({ access_token: _accessToken });

  const body = {
    title,
    description: description || 'Work on this exploration with Graspable Math.',
    workType: 'ASSIGNMENT',
    state,
    materials: [{ link: { url } }],
  };

  // Add due date if provided: { year, month, day } and { hours, minutes }
  if (dueDate) {
    body.dueDate = dueDate;
    body.dueTime = dueTime || { hours: 23, minutes: 59 };
  }

  const res = await gapi.client.classroom.courses.courseWork.create({ courseId }, body);
  return res.result;
}
